import { z } from "zod";
import { BRAND_KEYS } from "../brand/brand.js";
import { PRODUCT_GALLERY_MAX_FILES, PRODUCT_MEDIA_DESCRIPTION_MAX, PRODUCT_MEDIA_KINDS, } from "./agent-media.js";
import { deploymentEffortSchema, deploymentMaxTokensSchema, selectableModelIdSchema, } from "./models.js";
export * from "./agent-media.js";
export * from "./catalog.js";
export * from "./conversation-script.js";
export * from "./delivery-zones.js";
export * from "./lead-assignment.js";
export * from "./legal.js";
export * from "./models.js";
export * from "./notifications.js";
export * from "./promotions.js";
export * from "./reengagement.js";
export * from "./site.js";
export * from "./site-ui.js";
export * from "./visits.js";
export * from "./waba-templates.js";
export * from "./whatsapp-broadcasts.js";
export * from "./whatsapp-opt-out.js";
import { hasControlChars, hasReservedXmlTag } from "../security/sanitize.js";
// Value import (not type-only): the Zod enum is built from the same tuple the
// PipelineStage DTO is typed by, so the two can never drift.
import { STAGE_ROLES } from "../types/index.js";
export const channelSchema = z.enum(["web", "whatsapp", "instagram"]);
export const messageRoleSchema = z.enum(["user", "assistant", "human_agent"]);
export const messageTypeSchema = z.enum(["text", "voice_note", "document"]);
// A stage is now a per-client pipeline column key (see `pipeline_stages`), not a
// fixed enum. The value is validated for membership against the client's columns
// in pipeline-stage.service on the backend — here it's just a bounded string.
export const leadStageSchema = z.string().min(1).max(64);
export const alertTypeSchema = z.enum([
    "score_threshold",
    "human_requested",
    "followup_30min",
]);
export const crmRoleSchema = z.enum([
    "admin",
    "member",
    "super_admin",
    "account_manager",
]);
export const resourceStatusSchema = z.enum(["draft", "active", "archived"]);
// WhatsApp transport per client (mirrors the Prisma `WhatsappTransport` enum). The CRM can't import
// @prisma/client, so this is the shared source of truth for the type + UI labels + the super-admin
// update-transport request body. See docs/plans/whatsapp-official-api-onboarding.md.
export const whatsappTransportSchema = z.enum([
    "baileys",
    "cloud_api",
    "cloud_api_coexistence",
]);
export const WHATSAPP_TRANSPORT_LABELS = {
    baileys: "Baileys",
    cloud_api: "Cloud API",
    cloud_api_coexistence: "Cloud API (coexistence)",
};
export const updateTransportSchema = z.object({
    transport: whatsappTransportSchema,
});
// Body of POST /super-admin/clients/:clientId/deployments. This route ran with NO
// schema until `model` existed, and the controller spreads the body straight into
// the repository — tolerable while every field was free text that only the agent's
// wording depended on. It stops being tolerable with `model`: an unvalidated string
// reaches the DB and then every turn for that tenant fails against the Anthropic
// API. The enum is the only thing between a typo and a dead agent.
//
// Which conversation engine runs this deployment's turns.
//
//   v1 → the original single-phase loop (`aiService.streamChat`), untouched.
//   v2 → act-then-respond: every action runs first, then the reply is written ONCE
//        with the results in hand.
//
// Null means v1. This is not a model setting and must not be confused with one: v2
// exists because the v1 loop's stop conditions encode one model's habits, and three
// production turns (HC-900, DH-904, NH-784) showed what happens when a model does
// not share them — a reply overwritten mid-turn, an empty bubble, reasoning leaking
// into the prose.
export const DEPLOYMENT_ENGINES = ["v1", "v2"];
export const deploymentEngineSchema = z.enum(DEPLOYMENT_ENGINES);
export const ENGINE_LABELS = {
    v1: "v1 — motor original",
    v2: "v2 — acciones y luego respuesta",
};
// NULL AND OMITTED ARE DIFFERENT, and the optional fields are `nullish` so a caller
// can say which it means:
//
//   null      → "no override: use the model/system default"
//   (omitted) → "inherit whatever the active deployment has"
//
// `createNextVersion` honours both. The CRM sends an explicit null for every field
// it SHOWS and the user left blank, and omits only the fields it does not show
// (`notes`). Collapsing the two is not cosmetic: the editor's max_tokens placeholder
// promises "vacío = el default del modelo", and while blank meant omitted it
// inherited the previous version's value instead — a deployment created to get 4096
// silently came back as 2048, three versions in a row.
export const createDeploymentSchema = z.object({
    agentName: z.string().min(1).max(64),
    agentTone: z.string().max(200).nullish(),
    agentRole: z.string().max(120).nullish(),
    model: selectableModelIdSchema.nullish(),
    maxTokens: deploymentMaxTokensSchema.nullish(),
    // Only has an effect on a model whose catalog entry runs thinking; the CRM hides
    // the control otherwise rather than storing a value nothing reads.
    effort: deploymentEffortSchema.nullish(),
    engine: deploymentEngineSchema.nullish(),
    notes: z.string().max(2000).nullish(),
    knowledgeBaseId: z.string().uuid(),
    scriptId: z.string().uuid(),
});
// ----- Super-admin: client management -----
// Plan names are DB-driven (served by GET /super-admin/plans), so there are no
// shared plan constants — only the request-body shapes live here.
export const clientSlugSchema = z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/, "El slug solo permite minúsculas, números y guiones");
// A browser origin (scheme://host[:port]) allowed to embed the widget. Accepts a
// full URL and normalizes to its origin, so what we store matches the `Origin`
// header a browser sends exactly (the CORS check is an exact string compare).
export const widgetOriginSchema = z
    .string()
    .trim()
    .min(1)
    .transform((v, ctx) => {
    let url;
    try {
        url = new URL(v);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Origen inválido: ${v}` });
        return z.NEVER;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `El origen debe usar http o https: ${v}`,
        });
        return z.NEVER;
    }
    return url.origin;
});
// Client segment. Mirrors the `ClientType` Prisma enum — keep in sync.
// Selects the onboarding KB template and the suggested script; it is NOT the
// client's offering (that lives in the catalog). Each segment belongs to exactly
// one brand (`CLIENT_TYPE_PROFILES`): the first six are ScholaOps' schools
// (`k12_school` is a multi-level colegio, `high_school` is prepa-only), the last
// four are Loxago's businesses.
export const clientTypeSchema = z.enum([
    "preschool",
    "k12_school",
    "high_school",
    "university",
    "technical_trades",
    "academy",
    "retail_store",
    "trading_company",
    "machinery_dealer",
    "general_sales",
]);
// Which commercial brand a client is served under. Mirrors the `ClientBrand`
// Prisma enum, and its members come from `BRAND_KEYS` so the registry and the
// validator cannot drift — a brand the code can render but not validate, or the
// reverse, is the failure this spelling prevents.
//
// Needed as a Zod enum and not only as a TS union because `brand` crosses two
// untyped boundaries: the CRM's client form and `channel-health.repository.ts`'s
// hand-written `$queryRaw`, where it arrives as a plain string.
export const brandKeySchema = z.enum(BRAND_KEYS);
// Spanish labels for the segment. Here rather than in the CRM so the backend's KB
// templates and the client form cannot drift apart. Total record — a new segment
// fails to compile until it is labeled.
export const CLIENT_TYPE_LABELS = {
    preschool: "Preescolar (guardería, kinder, Montessori)",
    k12_school: "Colegio multinivel (primaria, secundaria y prepa)",
    high_school: "Preparatoria / Bachillerato",
    university: "Universidad (licenciatura, maestría, posgrado)",
    technical_trades: "Carreras técnicas y oficios",
    academy: "Academia (arte, deportes, idiomas)",
    retail_store: "Tienda o restaurante (envío a domicilio o para recoger)",
    trading_company: "Comercializadora / distribuidora",
    machinery_dealer: "Maquinaria y equipo",
    general_sales: "Ventas en general (CRM)",
};
const SCHOOL_PROFILE = {
    brand: "scholaops",
    suggestedScriptUseCase: "admisiones_escolares",
    openEnrollment: false,
};
export const CLIENT_TYPE_PROFILES = {
    preschool: SCHOOL_PROFILE,
    k12_school: SCHOOL_PROFILE,
    high_school: SCHOOL_PROFILE,
    university: SCHOOL_PROFILE,
    technical_trades: SCHOOL_PROFILE,
    academy: { ...SCHOOL_PROFILE, openEnrollment: true },
    retail_store: {
        brand: "general",
        suggestedScriptUseCase: "ventas_tienda_domicilio",
        openEnrollment: true,
    },
    trading_company: {
        brand: "general",
        suggestedScriptUseCase: "ventas_comercializadora",
        openEnrollment: true,
    },
    machinery_dealer: {
        brand: "general",
        suggestedScriptUseCase: "ventas_maquinaria",
        openEnrollment: true,
    },
    general_sales: { brand: "general", suggestedScriptUseCase: null, openEnrollment: true },
};
/** The segments a client of this brand may be filed under, in display order. */
export function clientTypesForBrand(brand) {
    return clientTypeSchema.options.filter((t) => CLIENT_TYPE_PROFILES[t].brand === brand);
}
export function isClientTypeAllowedForBrand(clientType, brand) {
    return CLIENT_TYPE_PROFILES[clientType].brand === brand;
}
export function clientTypeFamily(clientType) {
    return CLIENT_TYPE_PROFILES[clientType].brand === "scholaops" ? "school" : "business";
}
// A segment and a brand that do not belong together. Shared by both client
// schemas; the update path also checks against the STORED brand when the body
// carries only the segment (the controller does that, the schema cannot).
function refineBrandSegment(value, ctx) {
    if (!value.clientType || !value.brand)
        return;
    if (isClientTypeAllowedForBrand(value.clientType, value.brand))
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["clientType"],
        message: "El tipo de cliente no corresponde a la marca elegida.",
    });
}
// IANA zone (e.g. "America/Mexico_City"). Null falls back to the VISIT_TIMEZONE env.
const clientTimezoneSchema = z.string().trim().min(1).max(64);
const clientCitySchema = z.string().trim().min(1).max(120);
export const createClientSchema = z.object({
    name: z.string().trim().min(2).max(255),
    slug: clientSlugSchema,
    planId: z.string().uuid().nullish(),
    widgetOrigins: z.array(widgetOriginSchema).max(20).default([]),
    clientType: clientTypeSchema.nullish(),
    city: clientCitySchema.nullish(),
    timezone: clientTimezoneSchema.nullish(),
    // Which brand this client is served under. Optional on the wire and defaulted
    // by the column, so an older caller keeps working; not nullish, because unlike
    // `clientType` there is no unbranded state for a client to sit in.
    brand: brandKeySchema.optional(),
}).superRefine(refineBrandSegment);
export const updateClientSchema = z.object({
    name: z.string().trim().min(2).max(255),
    planId: z.string().uuid().nullable(),
    widgetOrigins: z.array(widgetOriginSchema).max(20).default([]),
    clientType: clientTypeSchema.nullish(),
    city: clientCitySchema.nullish(),
    timezone: clientTimezoneSchema.nullish(),
    brand: brandKeySchema.optional(),
}).superRefine(refineBrandSegment);
export const setClientActiveSchema = z.object({
    isActive: z.boolean(),
});
// Assign (or clear, with null) the account manager who owns this school.
export const setClientAccountManagerSchema = z.object({
    accountManagerId: z.string().uuid().nullable(),
});
// ----- Super-admin: plan management -----
// A plan's stable programmatic key (like a client slug, but underscores to match
// the seeded keys `free_trial`, etc.). Immutable after creation.
export const planKeySchema = z
    .string()
    .trim()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9_]+$/, "La clave solo permite minúsculas, números y guiones bajos");
export const createPlanSchema = z.object({
    key: planKeySchema,
    name: z.string().trim().min(2).max(64),
    // Optional — the backend assigns the next position when omitted.
    position: z.number().int().min(0).optional(),
    featureIds: z.array(z.string().uuid()).max(50).default([]),
});
// No `key` — the plan's key is immutable after creation.
export const updatePlanSchema = z.object({
    name: z.string().trim().min(2).max(64),
    position: z.number().int().min(0).optional(),
    featureIds: z.array(z.string().uuid()).max(50).default([]),
});
export const setPlanActiveSchema = z.object({
    isActive: z.boolean(),
});
// ----- Super-admin: billing (Stripe link) -----
// The ONLY billing field a human writes. Everything else in the mirror is written
// by the Stripe webhook — see docs/PRICING_AND_UNIT_ECONOMICS.md §9.
//
// `null` unlinks the client from its Stripe Customer. The `cus_` prefix is checked
// here because pasting a `sub_`, a `price_` or a Payment Link id is the realistic
// mistake, and it would silently route no events at all.
export const setStripeCustomerSchema = z.object({
    stripeCustomerId: z
        .string()
        .trim()
        .min(4)
        .max(64)
        .regex(/^cus_[A-Za-z0-9]+$/, "El ID de cliente de Stripe debe empezar con «cus_»")
        .nullable(),
});
// ----- Super-admin: generating a client's payment link -----
// Amounts are integer minor units (centavos), like everywhere else money crosses a
// boundary in this system. 1000 is Stripe's floor for MXN — below it the charge is
// rejected outright, so it is a hard minimum and not a policy choice.
//
// The commercial floor (Escuela's 6,990 band, §2.5 of the pricing doc) is
// deliberately NOT enforced here: the super-admin IS the house approval that
// section requires, so the CRM warns and this schema allows.
const MXN_MINIMUM_CENTS = 1_000;
export const createPaymentLinkSchema = z.object({
    productId: z
        .string()
        .trim()
        .regex(/^prod_[A-Za-z0-9]+$/, "Producto de Stripe inválido"),
    monthlyCents: z
        .number()
        .int()
        .min(MXN_MINIMUM_CENTS, "El mínimo que Stripe acepta en MXN es $10.00"),
    // Zero is valid and means "no implementation fee", producing a one-line link.
    setupCents: z.number().int().min(0),
    interval: z.enum(["month", "year"]),
    /**
     * Whether the amounts above are quoted before IVA or already contain it.
     *
     * `exclusive` is the public model ("9,990 + IVA") and the default, so an older
     * caller keeps behaving exactly as before. `inclusive` is the negotiated case —
     * "1,500 and that's it" — where the school pays the figure typed and the tax is
     * carved out of it.
     *
     * Neither turns tax off: `automatic_tax` stays on either way, and the invoice
     * shows the IVA line in both. What changes is which side of the amount it sits on.
     */
    taxBehavior: z.enum(["exclusive", "inclusive"]).default("exclusive"),
});
// ----- Client onboarding (wizard state) -----
// See docs/plans/client-onboarding-wizard.md. This resource stores only WHERE the
// operator got to; every step's real artifact (the client row, the catalog, the KB)
// is written by its own endpoint, so losing this row would cost progress tracking,
// never configuration.
/** The wizard's last step. 1–7 are gated by completion; 8–9 come after. */
export const ONBOARDING_FINAL_STEP = 7;
export const ONBOARDING_LAST_STEP = 9;
const onboardingStepSchema = z.number().int().min(1).max(ONBOARDING_LAST_STEP);
/** Roughly ten pages — more than any brochure, and ~5K tokens in the draft prompt. */
export const ONBOARDING_CONTEXT_MAX_CHARS = 20_000;
/**
 * Step 2 — everything the operator has about the client, unstructured.
 *
 * One free-text field rather than a set of questions, because that is how the material
 * arrives: a brochure, a paste of the website, a WhatsApp thread. Asking for it in
 * labelled boxes made the operator do the extraction work that step 4's model does
 * better, and in practice the boxes were used as dumping grounds anyway.
 *
 * Ordering it is step 4's job (`POST /super-admin/onboarding/:clientId/kb-draft`), which
 * turns this into a knowledge base shaped by the segment's template.
 *
 * NOT here: how the agent should sound. That is `agent_deployments.agent_tone`, captured
 * with the rest of the agent's identity in step 5 — a one-line answer the operator
 * already knows, and not something worth inferring from a brochure.
 *
 * Optional so a half-filled step survives navigating away; the wizard, not the schema,
 * decides when the step counts as complete.
 */
export const onboardingContextSchema = z.object({
    raw: z.string().trim().max(ONBOARDING_CONTEXT_MAX_CHARS).optional(),
});
export const updateOnboardingSchema = z.object({
    /** Where to resume. */
    currentStep: onboardingStepSchema.optional(),
    /** A step the operator just finished. Merged as a set — resending is a no-op. */
    completedStep: onboardingStepSchema.optional(),
    /** Replaces the stored context wholesale — see `onboardingService.patch`. */
    context: onboardingContextSchema.optional(),
    needsCustomScript: z.boolean().optional(),
});
/**
 * The step-4 knowledge base draft, which is a job rather than a response body.
 *
 * Generating one takes ~100s — longer than the proxy in front of the API tolerates on a
 * silent connection — so the request that starts it returns immediately and the draft is
 * written to `client_onboarding` when it lands, whether or not anyone is still connected.
 *
 * `idle` is the synthesized state for a client that has never generated one; it is not
 * stored (the column is simply null).
 */
export const kbDraftStatusSchema = z.enum(["idle", "running", "ready", "failed"]);
export const agentTypeSchema = z.enum(["admissions", "internal_faq", "store"]);
// ----- Chat API schemas -----
export const chatSessionRequestSchema = z.object({
    clientId: z.string().uuid(),
    // Everything here is untrusted text read from the host page's DOM. The widget
    // is expected to validate and OMIT a field it cannot make valid rather than
    // send something this schema rejects: a 400 here means the session is never
    // created, and the widget simply never opens on the school's homepage.
    initialContext: z
        .object({
        referrer: z.string().url().optional(),
        utmSource: z.string().max(64).optional(),
        pageUrl: z.string().url().max(2000).optional(),
        // The AI-first site's anonymous visitor (phase 4 of
        // docs/plans/ai-first-site.md): the uuid that joins this lead to its
        // `site_events`, and the first page of the visit, whose UTM tags the
        // server reads. Both absent on any other embed.
        visitorId: z.string().uuid().optional(),
        landingUrl: z.string().url().max(2000).optional(),
        // Attribution label for the CTA the visitor came in through. Stored on the
        // lead and shown in the CRM; never sent to the LLM. It IS untrusted text,
        // so never interpolate it into an HTML email or a prompt without escaping.
        entryPoint: z
            .string()
            .max(120)
            .refine((v) => !hasControlChars(v), "entryPoint contains disallowed control or invisible characters")
            .transform((v) => v.normalize("NFKC"))
            .optional(),
    })
        .optional(),
});
export const chatMessageRequestSchema = z.object({
    sessionRef: z
        .string()
        .max(8)
        .regex(/^[A-Z]{2}-\d{3,}$/, "sessionRef must look like ES-042"),
    content: z
        .string()
        .min(1)
        .max(2000)
        .refine((v) => !hasControlChars(v), "content contains disallowed control or invisible characters")
        .transform((v) => v.normalize("NFKC")),
    /**
     * The product the visitor wrote from on the AI-first site (its side panel or
     * page). Resolved by the server inside the lead's own client; an unknown slug
     * is ignored, never an error. The message text stays what the visitor typed.
     */
    productSlug: z
        .string()
        .max(200)
        .regex(/^[a-z0-9-]+$/)
        .optional(),
});
// ----- CRM schemas -----
/**
 * Text a CRM user sends to a lead over WhatsApp.
 *
 * 4096 is WhatsApp's own cap on a text message body — a longer one is rejected
 * by Graph, so it is rejected here where the author can still see why. Control
 * and invisible characters are refused rather than stripped: unlike an inbound
 * message (which we clean and keep, because dropping a prospect's words is
 * worse), this text has an author sitting in front of the box who can fix it.
 */
export const sendLeadReplySchema = z.object({
    content: z
        .string()
        .trim()
        .min(1)
        .max(4096)
        .refine((v) => !hasControlChars(v), "content contains disallowed control or invisible characters")
        .transform((v) => v.normalize("NFKC")),
    /**
     * Which chat the reply goes out on, for a lead reachable on both (a merged
     * WhatsApp + Instagram lead). Absent ⇒ the channel of the prospect's last
     * message — the conversation they are actually in.
     */
    channel: z.enum(["whatsapp", "instagram"]).optional(),
});
export const updateLeadStageSchema = z.object({
    stage: leadStageSchema,
});
// Fixed palette of colors shared by pipeline columns and per-lead accents. Stored
// by name; the CRM maps each to Tailwind classes (see apps/crm/src/lib/stage-colors.ts).
// Kept here so the backend validates the same set the UI offers.
export const PIPELINE_STAGE_COLORS = [
    "slate",
    "blue",
    "teal",
    "green",
    "amber",
    "orange",
    "rose",
    "purple",
];
export const pipelineStageColorSchema = z.enum(PIPELINE_STAGE_COLORS);
export const createLeadSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    phone: z.string().min(8).max(20).optional(),
    email: z.string().email().optional(),
    productId: z.string().uuid().optional(),
    cycleId: z.string().uuid().optional(),
    capturedFields: z.record(z.string(), z.string()).optional(),
});
export const updateLeadSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    phone: z.string().min(8).max(20).optional(),
    email: z.string().email().optional(),
    stage: leadStageSchema.optional(),
    productId: z.string().uuid().optional(),
    cycleId: z.string().uuid().optional(),
    capturedFields: z.record(z.string(), z.string()).optional(),
    // `null` clears the color; a palette name sets it. Reuses the pipeline palette.
    color: pipelineStageColorSchema.nullable().optional(),
})
    .refine((d) => Object.values(d).some((v) => v !== undefined), "At least one field is required");
/**
 * Knowledge base length bounds. Exported because the CRM enforces the same
 * limits in the editor: when they lived in both places as literals, raising one
 * left the other rejecting at the old value.
 */
export const KB_MIN_CHARS = 500;
export const KB_MAX_CHARS = 50_000;
export const updateKnowledgeBaseSchema = z.object({
    content: z
        .string()
        .min(KB_MIN_CHARS, `Knowledge base must be at least ${KB_MIN_CHARS} characters`)
        .max(KB_MAX_CHARS, `Knowledge base must be at most ${KB_MAX_CHARS} characters`)
        .refine((v) => !hasReservedXmlTag(v), "Knowledge base cannot contain XML closing tags reserved for system use")
        .refine((v) => !hasControlChars(v), "Knowledge base contains disallowed control characters")
        .transform((v) => v.normalize("NFKC")),
});
// Title and description are injected into the system prompt, so they get the
// same anti-injection treatment as the knowledge base content.
const promptSafeText = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((v) => !hasReservedXmlTag(v), "Cannot contain XML closing tags reserved for system use")
    .refine((v) => !hasControlChars(v), "Contains disallowed control characters")
    .transform((v) => v.normalize("NFKC"));
// ----- Pipeline stages (configurable Kanban columns) -----
export const createPipelineStageSchema = z.object({
    label: promptSafeText(64),
    color: pipelineStageColorSchema.optional(),
    requiresReason: z.boolean().optional(),
});
// `stageRole` is deliberately absent from `create`: a column is born roleless and
// is promoted afterwards, so adding a column can never displace the conversion one
// by accident. `null` clears the role — the service still refuses to clear the
// conversion column itself, since having none is the state that breaks reporting.
export const updatePipelineStageSchema = z
    .object({
    label: promptSafeText(64).optional(),
    color: pipelineStageColorSchema.nullable().optional(),
    requiresReason: z.boolean().optional(),
    stageRole: z.enum(STAGE_ROLES).nullable().optional(),
})
    .refine((d) => Object.values(d).some((v) => v !== undefined), "At least one field is required");
export const reorderPipelineStagesSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
// ----- Order board columns (custom sub-stages of "Por entregar") -----
export const createOrderBoardStageSchema = z.object({
    label: promptSafeText(64),
    color: pipelineStageColorSchema.optional(),
});
export const updateOrderBoardStageSchema = z
    .object({
    label: promptSafeText(64).optional(),
    // `null` clears the colour; absent leaves it unchanged.
    color: pipelineStageColorSchema.nullable().optional(),
})
    .refine((d) => Object.values(d).some((v) => v !== undefined), "At least one field is required");
export const reorderOrderBoardStagesSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
/** Moves a confirmed order between "Por entregar" (null) and a custom column. */
export const setOrderBoardStageSchema = z.object({
    boardStageId: z.string().uuid().nullable(),
});
// ----- Lead labels (colored tags attached to leads, many-to-many) -----
export const createLeadLabelSchema = z.object({
    name: promptSafeText(40),
    color: pipelineStageColorSchema.optional(),
});
export const updateLeadLabelSchema = z
    .object({
    name: promptSafeText(40).optional(),
    // `null` clears the color; a palette name sets it.
    color: pipelineStageColorSchema.nullable().optional(),
})
    .refine((d) => Object.values(d).some((v) => v !== undefined), "At least one field is required");
export const assignLeadLabelSchema = z.object({
    labelId: z.string().uuid(),
});
// Registers a program of interest on a lead. The server rejects a product from
// another tenant (404) and an archived one (400) — neither is expressible here.
export const addLeadInterestSchema = z.object({
    productId: z.string().uuid(),
});
// A person the lead is enquiring FOR. `name` is required because it is the
// upsert identity the AI path matches on — a nameless row could never be
// reconciled with what a later turn says.
const CANDIDATE_MIN_AGE = 1;
const CANDIDATE_MAX_AGE = 99;
export const createLeadCandidateSchema = z.object({
    name: promptSafeText(80),
    age: z.number().int().min(CANDIDATE_MIN_AGE).max(CANDIDATE_MAX_AGE).nullable().optional(),
    gradeLabel: promptSafeText(60).nullable().optional(),
    relationship: z.enum(["child", "other"]).optional(),
    productId: z.string().uuid().nullable().optional(),
});
// Every field optional: the form patches one at a time. `null` clears, an
// absent key leaves the column alone — the distinction the service relies on.
export const updateLeadCandidateSchema = createLeadCandidateSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "Nothing to update" });
// A document is GENERAL: no product. A product's own files are product media
// (docs/plans/product-media.md). `productId` is not accepted any more — and not
// rejected either: `z.object` strips unknown keys, so a CRM tab opened before
// this release that still sends one saves fine, without the link.
export const documentMetaSchema = z.object({
    title: promptSafeText(120),
    description: promptSafeText(500),
});
export const updateDocumentSchema = z.object({
    title: promptSafeText(120).optional(),
    description: promptSafeText(500).optional(),
    isActive: z.boolean().optional(),
});
// ----- Product media -----
export const productMediaKindSchema = z.enum(PRODUCT_MEDIA_KINDS);
/** Reaches the cached prompt prefix, hence promptSafeText. Empty clears it. */
export const updateProductMediaSchema = z.object({
    description: z.union([promptSafeText(PRODUCT_MEDIA_DESCRIPTION_MAX), z.literal("").transform(() => null), z.null()]),
});
export const reorderProductGallerySchema = z.object({
    fileIds: z.array(z.string().uuid()).min(1).max(PRODUCT_GALLERY_MAX_FILES),
});
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ----- User management schemas -----
/**
 * The roles assignable inside a school's own roster, and the mirror of
 * `platformRoleSchema` below.
 *
 * Deliberately NOT `crmRoleSchema`: these schemas back `/crm/users`, which any
 * school admin can reach. While the full enum was accepted there, one
 * `PATCH /crm/users/<own id>` with `role: "super_admin"` promoted a tenant admin
 * to platform super-admin — every client's data, the usage dashboard, script
 * editing and billing, in a single request. Platform roles are granted only
 * through `/super-admin/platform-users`, whose accounts carry no clientId.
 *
 * This also matches what the CRM has always offered: its two role dropdowns on
 * this page list Miembro and Admin and nothing else.
 */
export const clientRoleSchema = z.enum(["admin", "member"]);
export const inviteUserSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(255),
    role: clientRoleSchema,
});
export const updateUserSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    role: clientRoleSchema.optional(),
})
    .refine((d) => d.name !== undefined || d.role !== undefined, "At least one field (name or role) is required");
// ----- Platform (super-admin) user management schemas -----
// Platform users are Profile rows with clientId=null. Two platform roles exist:
// `super_admin` (full platform control) and `account_manager` (a sales role
// scoped to the clients where Client.accountManagerId === their profile id).
// Future roles (e.g. soporte) extend this enum (and the Prisma CrmRole enum +
// the CRM role dropdown).
export const platformRoleSchema = z.enum(["super_admin", "account_manager"]);
export const invitePlatformUserSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(255),
    role: platformRoleSchema,
});
export const updatePlatformUserSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    role: platformRoleSchema.optional(),
})
    .refine((d) => d.name !== undefined || d.role !== undefined, "At least one field (name or role) is required");
// ----- Catalog schemas (formerly agent-config) -----
// The catalog is saved one row at a time; the per-row bodies live in
// schemas/catalog.ts next to the row schemas they are built from.
export const followupOptionSchema = z.object({
    label: z.string().min(1).max(200),
    prompt: z.string().min(1).max(200),
});
// Widget primary color: a 6-digit hex like "#1d341e". Constrained to hex so the
// value is safe to inject as a CSS custom property in the widget.
export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Debe ser un color hex de 6 dígitos, ej. #1d341e");
/**
 * The order of one catalog list, saved the moment an arrow is pressed.
 * `position` is the row's index in the editor's list; rows not created yet (no
 * id) are simply not sent.
 */
export const CATALOG_ORDER_LISTS = ["products", "cycles", "customFields", "categories"];
export const saveCatalogOrderSchema = z.object({
    list: z.enum(CATALOG_ORDER_LISTS),
    order: z
        .array(z.object({ id: z.string().uuid(), position: z.number().int().min(0) }))
        .max(1000),
});
// ----- Client settings -----
// Agent/widget presentation config. These used to ride on the `catalogs`
// version row and on `PUT /crm/catalog`, which meant changing a colour ran the
// whole catalog diff. They are per-client settings, so they get their own
// resource. See docs/plans/catalog-forms-redesign.md.
/** Max length of the widget's opening message. Matches `clients.greeting`. */
export const GREETING_MAX_LENGTH = 300;
/**
 * Placeholders the greeting may use. Substituted at render time by
 * `renderGreeting()` in the backend's prompt builder; the CRM shows the same
 * list as a hint, so both sides read it from here.
 */
export const GREETING_PLACEHOLDERS = ["agentName", "businessName"];
/**
 * Accepted and substituted, never advertised. `{schoolName}` was the business
 * token before it was renamed for every industry; every script version already
 * in `conversation_scripts` still carries it, so it must keep rendering.
 */
export const LEGACY_GREETING_PLACEHOLDERS = ["schoolName"];
const PLACEHOLDER_TOKEN_RE = /\{(\w*)\}/g;
/**
 * True when every `{token}` in the text is one we substitute. An unknown token
 * is rejected on save rather than rendered literally — otherwise a typo like
 * `{nombre}` reaches the prospect verbatim in the very first message.
 */
export function hasOnlyKnownGreetingPlaceholders(value) {
    const known = [...GREETING_PLACEHOLDERS, ...LEGACY_GREETING_PLACEHOLDERS];
    return [...value.matchAll(PLACEHOLDER_TOKEN_RE)].every((match) => known.includes(match[1] ?? ""));
}
export const clientSettingsSchema = z.object({
    /** Quick-reply chips the widget offers before the first user message. */
    initialOptions: z.array(followupOptionSchema).max(3).nullable(),
    googleMapsUrl: z.string().url("Debe ser una URL válida").max(500).nullable(),
    /**
     * The video-call link for online appointments — the invitation's location.
     * http(s) only: it is rendered as a link in an email and a calendar event.
     * Optional, unlike its siblings: a CRM tab opened before this field existed
     * still saves, and absent means "leave it as it is", never "clear it".
     */
    meetingUrl: z
        .string()
        .trim()
        .url("Debe ser una URL válida")
        .max(500)
        .refine((v) => /^https?:\/\//i.test(v), "Debe ser un enlace http(s)")
        .nullable()
        .optional(),
    /** null = fall back to DEFAULT_WIDGET_PRIMARY_COLOR. */
    primaryColor: hexColorSchema.nullable(),
    /** null = fall back to the active ConversationScript's greeting template. */
    greeting: promptSafeText(GREETING_MAX_LENGTH)
        .refine(hasOnlyKnownGreetingPlaceholders, "Solo se permiten {agentName} y {businessName}")
        .nullable(),
});
//# sourceMappingURL=index.js.map